import { createActionGroup, emptyProps, props } from '@ngrx/store';
import {
    AccountStateCommand,
    IAbcpSyncBody,
    IAbcpSyncResult,
    IAccountStatePatch,
    IAccountBindingWrite,
    IAccountBindingWriteResult,
    IClassificationSummary,
    IClosingSyncState,
    IPeriodBounds,
    IProjectAccount,
    IProjectAccountsResponse,
    IReconciliationStatusResponse,
} from '../models/project-account.model';

/**
 * The account screen's actions.
 *
 * Every one carries the `projectId`, including the successes. The screen can be left while a request
 * is in flight, and a late answer that does not say which closing it belongs to would overwrite the
 * accounts of the closing the user has since opened.
 */
export const projectAccountsActions = createActionGroup({
    source: 'Project Accounts',
    events: {
        // ── The list: expensive, one call per screen open and per comparison change ──
        'Load Accounts': props<{
            projectId: string;
            comparisonPeriods?: IPeriodBounds[];
            withZeroAccounts?: boolean;
        }>(),
        /**
         * "Show me this closing's accounts" — the page's request, not a command to fetch.
         *
         * Deliberately has NO reducer handler: reducers run before effects, so a handler that already
         * set `accountsStatus: 'loading'` would destroy the very information the guard reads. Its
         * effect decides whether a request is needed and, if so, dispatches `Load Accounts` with the
         * view the screen is currently on.
         */
        'Load Accounts If Needed': props<{ projectId: string }>(),

        /**
         * "Fetch the list again now" — unconditional, with the view the screen is on.
         *
         * Distinct from `Load Accounts If Needed`, which may decide the store already knows, and from
         * `Load Accounts`, which would answer the backend's default and collapse the chosen columns.
         * Dispatched when a screen KNOWS the server moved: after a batch of manual assignments.
         */
        'Reload Accounts': props<{ projectId: string }>(),
        'Load Accounts Success': props<{ projectId: string; response: IProjectAccountsResponse }>(),
        'Load Accounts Failure': props<{ projectId: string; error: string }>(),

        // ── The step list: the DEFAULT view of the list, read by the navigation, the Jahresabschluss overview and the guard ──
        /**
         * "I need the default list" — the navigation's request, not a command to fetch.
         *
         * No reducer handler, for the reason `Load Accounts If Needed` gives. Its effect skips the request
         * when the step list is there, takes the shared list over when that one already IS the default
         * view, and only then dispatches `Load Step Accounts`.
         */
        'Load Step Accounts If Needed': props<{ projectId: string }>(),
        /** Always the default view (prior year alone, zero accounts left out), whatever the screens show. */
        'Load Step Accounts': props<{ projectId: string }>(),
        'Load Step Accounts Success': props<{ projectId: string; accounts: readonly IProjectAccount[] }>(),
        'Load Step Accounts Failure': props<{ projectId: string; error: string }>(),

        // ── Which periods we HOLD a journal for: cheap, metadata only. The chips come from this ──
        'Load Journal Periods': props<{ projectId: string }>(),
        'Load Journal Periods Success': props<{ projectId: string; state: IClosingSyncState }>(),
        'Load Journal Periods Failure': props<{ projectId: string; error: string }>(),

        /**
         * A comparison chip was clicked.
         *
         * Separate from `Load Accounts` on purpose: the selection is state the screen keeps, and the
         * reload is a consequence of it. Folding the two would make the chosen set unreadable while a
         * request is in flight, and the chips would flicker back on every answer.
         */
        'Select Comparison Periods': props<{ projectId: string; comparisonPeriods: IPeriodBounds[] }>(),

        /**
         * Show or hide accounts that are zero everywhere.
         *
         * Its own action for the same reason the comparison selection has one: it is state the screen
         * keeps, and the refetch is a consequence. It IS a refetch — the filter runs in the backend,
         * over figures this library never sees in full.
         */
        'Set With Zero Accounts': props<{ projectId: string; withZeroAccounts: boolean }>(),

        /** The workspace header's search on the `Konten` tab (REV-22006). Filters in the client. */
        'Set Search Term': props<{ searchTerm: string }>(),

        // ── The counters: cheap, this is the one that may be polled ──
        'Load Summary': props<{ projectId: string }>(),
        'Load Summary Success': props<{ projectId: string; summary: IClassificationSummary }>(),
        'Load Summary Failure': props<{ projectId: string; error: string }>(),

        /**
         * The classification has been outstanding past the polling window — stop asking and say so.
         *
         * Its own action rather than a field on a failure: nothing failed. The request was accepted,
         * it simply never came back, and that is a different statement to make to a user.
         */
        'Classification Overdue': props<{ projectId: string }>(),

        // ── The reconciliation poll (#162, AP8a): cheap, self-scheduling like `Load Summary` ──
        /**
         * One tick of the reconciliation poll. `tick` and `elapsedMs` travel WITH the action rather
         * than living on an effect instance, exactly like the classification poll's `waitedMs`
         * (`ageOf`) — the chain is self-scheduling, so the next tick's cadence and ceiling check are
         * decided from what THIS action already knows, not from a place that could forget between
         * dispatches.
         */
        /**
         * `consecutiveFailures` travels the same way `tick`/`elapsedMs` do (#162 round 4, SRR F1): a
         * failure streak has to survive a success (which resets it to 0) and end the chain at three in
         * a row, and the only place that count can live is the action itself — the chain is
         * self-scheduling and has no instance field to keep it in between dispatches.
         */
        /**
         * REV-22003 — the AI filed a document THIS SESSION uploaded straight to an account (auto-assign),
         * so a reconciliation run is on its way for it. The third way into `reconciliationWatch`, next to
         * the success of `Assign Document` / `Accept Proposal`: without it the account's result only
         * showed up after a reload. Also sent when an upload from the account page lands, because the
         * server files it in the upload request itself.
         */
        'Watch Reconciliation': props<{ projectId: string; accountIds: string[] }>(),
        'Load Reconciliation Status': props<{
            projectId: string;
            tick: number;
            elapsedMs: number;
            consecutiveFailures: number;
        }>(),
        'Load Reconciliation Status Success': props<{
            projectId: string;
            tick: number;
            elapsedMs: number;
            consecutiveFailures: number;
            status: IReconciliationStatusResponse;
        }>(),
        // Deliberately NOT `Load Reconciliation Status Failure` alone stopping the chain
        // (spec:GH-162 S5): the counter route is #160's and answers 404/500 on the running stack today,
        // and a poll that gave up on the first failure would never see #160 ship at all. THREE in a row
        // does end it (round 4, SRR F1) — see `consecutiveFailures` above.
        'Load Reconciliation Status Failure': props<{
            projectId: string;
            tick: number;
            elapsedMs: number;
            consecutiveFailures: number;
        }>(),
        /**
         * The chain stopped: nothing is outstanding any more (`POLL_STOPS_WHEN` in the e2e fixture), or
         * the tick ceiling was reached. Its own action rather than a field on success/failure — the
         * double-start guard (`reconciliationPolling`) needs one signal that fires from EITHER ending,
         * and folding it into both actions would be two places to keep in sync.
         */
        'Reconciliation Poll Ended': props<{ projectId: string }>(),

        // ── Manual assignment ──
        'Write Bindings': props<{ projectId: string; bindings: IAccountBindingWrite[] }>(),
        // The list is reloaded on success rather than patched in place: the backend derives the
        // classification state from the binding, and guessing that here would be a second source.
        'Write Bindings Success': props<{ projectId: string; result: IAccountBindingWriteResult }>(),
        'Write Bindings Failure': props<{ projectId: string; error: string }>(),

        // ── REV-22005: a trustee's change to one account's state (review flag, document value) ──
        /**
         * One write per dispatch. The success carries the row's NEW state and the reducer patches that
         * row — no list reload: the server already said what changed, and a reload would decrypt a
         * journal per comparison period for it.
         */
        'Write Account State': props<{ projectId: string; accountId: string; command: AccountStateCommand }>(),
        'Write Account State Success': props<{ projectId: string; patch: IAccountStatePatch }>(),
        'Write Account State Failure': props<{ projectId: string; accountId: string; error: string }>(),
        /** The dialog was closed — its error belongs to it and must not greet the next one. */
        'Reset Account State Write': emptyProps(),

        // ── The ERP pull of this closing ──
        Sync: props<{ projectId: string; body: IAbcpSyncBody }>(),
        /**
         * REV-22007 — the re-import of a FILE closing from an uploaded trial balance (ADR-0020).
         *
         * Answers with the SAME `Sync Success` / `Sync Failure` as the vendor pull: the progress modal,
         * the summary and the reload after it are one flow, and a second pair of outcome actions would
         * be a second place for them to drift apart.
         */
        'Sync From File': props<{ projectId: string; fileId: string }>(),
        'Sync Success': props<{ projectId: string; result: IAbcpSyncResult }>(),
        'Sync Failure': props<{ projectId: string; error: string }>(),
        /** The summary or the dialog was closed — its outcome must not reopen on the next visit to the closing. */
        'Reset Sync': emptyProps(),
    },
});
